/**
 * Documents view.
 * Stacked list/detail pattern — file list → tap file → full-height preview with back button.
 */

import { createElement, useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { ArrowLeft, Download, FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { GcsFileInfo } from '@/client/app/hooks/useAccountFiles';
import { useFiles, FILES_API_BASE } from '@/client/app/hooks/useFiles';
import { getCustomHeaders } from '@/client/lib/api';
import { formatBytes, formatDate, fileIcon, groupFiles, SECTION_LABELS } from '@/client/lib/document-utils';
import { MarkdownContent } from '@/client/components/MarkdownContent';

/* ── Download helper ──────────────────────────────────────────── */

function downloadFile(file: GcsFileInfo, fetchUrl: string, headers?: Record<string, string>) {
  fetch(fetchUrl, headers ? { headers } : undefined)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    })
    .catch((err) => console.error('Download failed:', err));
}

/* ── File Card (full-width for widget) ────────────────────────── */

function EmbedFileCard({
  file,
  onSelect,
  onDelete,
}: {
  file: GcsFileInfo;
  onSelect: (file: GcsFileInfo) => void;
  onDelete: (path: string) => void;
}) {
  const icon = fileIcon(file.contentType);

  const handleDownload = (e: MouseEvent) => {
    e.stopPropagation();
    downloadFile(
      file,
      `${FILES_API_BASE}/content?path=${encodeURIComponent(file.path)}`,
      getCustomHeaders(),
    );
  };

  const handleDelete = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await fetch(
        `${FILES_API_BASE}?path=${encodeURIComponent(file.path)}`,
        { method: 'DELETE', headers: getCustomHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onDelete(file.path);
      toast.success('Document deleted');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete document');
    }
  };

  return (
    <button
      onClick={() => onSelect(file)}
      className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors bg-card hover:bg-accent"
    >
      {createElement(icon, {
        className: 'size-5 shrink-0 text-muted-foreground',
      })}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{file.title || file.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatBytes(file.size)} &middot; {formatDate(file.updated)}
          {file.author && <> &middot; {file.author}</>}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        <button
          onClick={handleDownload}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          aria-label={`Download ${file.name}`}
        >
          <Download className="size-4" />
        </button>
        <button
          onClick={handleDelete}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          aria-label={`Delete ${file.name}`}
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </button>
  );
}

/* ── CSV Table ────────────────────────────────────────────────── */

function parseCsvRow(row: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (inQuotes) {
      if (ch === '"' && row[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      cells.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
}

function CsvTable({ csv }: { csv: string }) {
  const lines = csv.split('\n').filter((l) => l.trim());
  if (lines.length === 0) return <p className="text-sm text-muted-foreground">Empty CSV</p>;

  const headers = parseCsvRow(lines[0]);
  const rows = lines.slice(1).map(parseCsvRow);

  return (
    <div className="overflow-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="border-b bg-muted/50 px-3 py-2 text-left font-medium text-muted-foreground">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-b last:border-0 hover:bg-muted/30">
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-1.5 text-foreground">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── File Preview (full-height with back button) ──────────────── */

function EmbedFilePreview({
  file,
  onBack,
  onDelete,
}: {
  file: GcsFileInfo;
  onBack: () => void;
  onDelete: (path: string) => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isMarkdown = file.name.endsWith('.md');
  const isImage = file.contentType.startsWith('image/');
  const isHtml = !isMarkdown && (file.contentType.includes('html') || file.name.endsWith('.html'));
  const isCsv = file.contentType.includes('csv') || file.name.endsWith('.csv');

  useEffect(() => {
    let revoke: string | null = null;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    setTextContent(null);

    (async () => {
      try {
        const res = await fetch(
          `${FILES_API_BASE}/content?path=${encodeURIComponent(file.path)}`,
          { headers: getCustomHeaders() },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        if (isMarkdown || isCsv) {
          const text = await res.text();
          setTextContent(text);
        } else {
          const blob = await res.blob();
          revoke = URL.createObjectURL(blob);
          setBlobUrl(revoke);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [file.path, isMarkdown, isCsv]);

  return (
    <div className="flex h-full flex-col">
      {/* Header with back button */}
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <button
          onClick={onBack}
          className="flex size-7 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-muted"
          aria-label="Back to file list"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{file.title || file.name}</p>
          <p className="text-xs text-muted-foreground">
            {formatBytes(file.size)} &middot; {formatDate(file.updated)}
            {file.author && <> &middot; {file.author}</>}
          </p>
        </div>
        <button
          onClick={() =>
            downloadFile(
              file,
              `${FILES_API_BASE}/content?path=${encodeURIComponent(file.path)}`,
              getCustomHeaders(),
            )
          }
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label={`Download ${file.name}`}
        >
          <Download className="size-4" />
        </button>
        <button
          disabled={deleting}
          onClick={async () => {
            setDeleting(true);
            try {
              const res = await fetch(
                `${FILES_API_BASE}?path=${encodeURIComponent(file.path)}`,
                { method: 'DELETE', headers: getCustomHeaders() },
              );
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              toast.success('Document deleted');
              onDelete(file.path);
            } catch (err) {
              console.error('Delete failed:', err);
              toast.error('Failed to delete document');
              setDeleting(false);
            }
          }}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          aria-label={`Delete ${file.name}`}
        >
          <Trash2 className="size-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {loading && <p className="text-sm text-muted-foreground">Loading preview...</p>}
        {error && <p className="text-sm text-destructive">Failed to load: {error}</p>}
        {textContent != null && isMarkdown && <MarkdownContent content={textContent} />}
        {blobUrl && isImage && (
          <img src={blobUrl} alt={file.name} className="max-w-full rounded-md" />
        )}
        {blobUrl && isHtml && (
          <iframe src={blobUrl} title={file.name} className="h-full w-full rounded-md border" />
        )}
        {textContent != null && isCsv && <CsvTable csv={textContent} />}
        {blobUrl && !isImage && !isHtml && !isCsv && (
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              No preview available for this file type.
            </p>
            <a
              href={blobUrl}
              download={file.name}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90"
            >
              <Download className="size-3" />
              Download
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main View ────────────────────────────────────────────────── */

export function DocumentsView({
  initialFilePath,
  onInitialFileConsumed,
}: {
  /** When set, auto-open this file (e.g. from a chat asset "View" tap). */
  initialFilePath?: string | null;
  /** Called after the initial file path has been consumed. */
  onInitialFileConsumed?: () => void;
}) {
  const { files, loading, error, removeFile } = useFiles();
  const [selectedFile, setSelectedFile] = useState<GcsFileInfo | null>(null);
  const consumedRef = useRef<string | null>(null);

  const handleDelete = useCallback(
    (path: string) => {
      removeFile(path);
      if (selectedFile?.path === path) setSelectedFile(null);
    },
    [removeFile, selectedFile],
  );

  // Auto-select file when navigated from a chat asset
  useEffect(() => {
    if (!initialFilePath || files.length === 0 || consumedRef.current === initialFilePath) return;
    consumedRef.current = initialFilePath;
    const match = files.find((f) => f.path === initialFilePath);
    if (match) setSelectedFile(match);
    onInitialFileConsumed?.();
  }, [initialFilePath, files, onInitialFileConsumed]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading documents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-destructive">Failed to load documents: {error.message}</p>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-muted-foreground">
        <FileText className="size-10 opacity-30" />
        <p className="text-sm font-medium">No documents yet</p>
        <p className="text-xs">Documents created by the agent will appear here.</p>
      </div>
    );
  }

  if (selectedFile) {
    return (
      <EmbedFilePreview
        key={selectedFile.path}
        file={selectedFile}
        onBack={() => setSelectedFile(null)}
        onDelete={handleDelete}
      />
    );
  }

  const groups = groupFiles(files);

  return (
    <div className="h-full space-y-4 overflow-auto p-4">
      {Object.entries(groups).map(([key, groupedFiles]) => {
        if (groupedFiles.length === 0) return null;
        return (
          <div key={key}>
            <h2 className="mb-2 text-xs font-medium text-muted-foreground">
              {SECTION_LABELS[key]} ({groupedFiles.length})
            </h2>
            <div className="space-y-1.5">
              {groupedFiles.map((file) => (
                <EmbedFileCard
                  key={file.path}
                  file={file}
                  onSelect={setSelectedFile}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
